import React, {createContext, useEffect, useMemo, useState} from 'react'
import {IAuthData, IUserInfo} from '../types/user'
import {AuthApi} from '../services/AuthApi.service'
import {CookiesService} from '../services/Cookie.service'
import {AssistantApi} from '../services/AssistantApi.service'
import {setSentryUser, clearSentryUser} from '../lib/sentry'
import {setOrganizationDefaultUrl} from '../utils/colors'

interface IAuthContext {
    login: (user: string, password: string, scope?: string) => void
    logout: () => void
    authData: IAuthData | null
    signUp: (
        firstName: string,
        lastName: string,
        email: string,
        phone: string,
        organization_name: string,
        password: string
    ) => Promise<void>
    userInfo: IUserInfo
}

const template = {
    created_at: new Date(),
    email: '',
    first_name: '',
    guid: '',
    is_active: true,
    org_admin: false,
    phone: '',
    last_name: '',
    last_session: new Date(),
    modified_at: new Date(),
    organization: {
        cnpj: '',
        created_at: new Date(),
        guid: '',
        name: '',
    },
}
export const AuthContext = createContext<IAuthContext>({
    login: () => {},
    logout: () => {},
    authData: null,
    signUp: async () => {},
    userInfo: template,
})

const AuthProvider = ({children}: {children: React.ReactNode}): JSX.Element => {
    const [authData, setAuthData] = useState<IAuthData | null>(null)
    const [userInfo, setUserInfo] = useState<IUserInfo>(template)
    const cookiesService = useMemo(() => {
        return new CookiesService()
    }, [])

    async function login(user: string, password: string, scope = '') {
        try {
            const api = new AuthApi()
            await api.login(user, password, scope)
            window.location.pathname = '/'
        } catch (error: any) {
            throw error
        }
    }

    async function getUserData() {
        try {
            const api = new AssistantApi()
            const userInfo = await api.getUser()
            if (setOrganizationDefaultUrl(userInfo.organization?.default_url)) {
                // tenant mudou: recarrega uma vez para cores/logos/tema usarem o default_url
                window.location.reload()
                return
            }
            setUserInfo(userInfo)
            setSentryUser({
                id: userInfo.guid,
                email: userInfo.email,
                name: `${userInfo.first_name} ${userInfo.last_name}`,
                organization: userInfo.organization.name,
            })
        } catch (error) {
            throw error
        }
    }

    async function signUp(
        firstName: string,
        lastName: string,
        email: string,
        user_data_phone: string,
        organization_name: string,
        password: string
    ) {
        try {
            const api = new AuthApi()
            await api.signUp(
                firstName,
                lastName,
                email,
                user_data_phone,
                organization_name,
                password
            )
            window.location.pathname = '/'
        } catch (error) {
            throw error
        }
    }

    function logout(): void {
        setAuthData(null)
        clearSentryUser()
        setOrganizationDefaultUrl(null)
        cookiesService.removeAuthData()
        cookiesService.removeAssistant()
        window.location.pathname = '/login'
    }

    useEffect(() => {
        const authdata = cookiesService.getAuthData()
        if (authdata.access_token) {
            setAuthData(authdata)
            getUserData()
            console.log('ok')
            return
        }
        console.log('not ok')
    }, [])

    return (
        <AuthContext.Provider
            value={{logout, login, signUp, authData, userInfo}}
        >
            {children}
        </AuthContext.Provider>
    )
}

export default AuthProvider
